/**
 * iter-231-V — Razorpay Linked Account form modal (Admin).
 *
 * Mounted from `RazorpayRouteDiagnoseCard`. Two modes:
 *   • mode="create" — modal opens with the EXACT payload
 *     `_build_account_payload` would compute (autofilled from
 *     vendor_tax_registration + vendor_bank_accounts + addresses).
 *     Admin can correct any missing/invalid field and submit. The
 *     submission hits POST .../route-account/create which calls
 *     Razorpay v2 /v2/accounts directly.
 *   • mode="edit"   — same flat form, prefilled from the current
 *     Razorpay account doc. Submit hits POST .../route-account/update
 *     which calls Razorpay PATCH /v2/accounts/{id}.
 *
 * Razorpay's own validation is the source of truth — we surface its
 * full error body inline beneath the failing fields.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Loader2, AlertCircle, CheckCircle2, Save, PlayCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const BUSINESS_TYPES = [
  'individual', 'proprietorship', 'partnership', 'private_limited',
  'public_limited', 'llp', 'ngo', 'trust', 'society', 'huf',
  'not_yet_registered', 'other',
];

// ──────────────────────────────────────────────────────────────────────
// FieldInput — MUST be defined outside the parent component, otherwise
// each parent re-render (which happens on every keystroke via setForm)
// creates a NEW component reference → React unmounts/remounts the
// underlying <input> → the user loses focus after each character. The
// production bug report on iter-231-V was: "each character input was
// difficult and it required to again and again click inside the input
// field for every character".
// ──────────────────────────────────────────────────────────────────────
const FieldInput = React.memo(function FieldInput({
  name, label, type = 'text', help, readOnly,
  value, onChange, errorMsg, required,
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={`rzpla-${name}`} className="text-xs">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <Input
        id={`rzpla-${name}`}
        type={type}
        value={value ?? ''}
        onChange={onChange}
        disabled={readOnly}
        className={errorMsg ? 'border-red-400 focus:border-red-500' : ''}
        data-testid={`rzpla-field-${name}`}
      />
      {errorMsg && <p className="text-[10px] text-red-600">{errorMsg}</p>}
      {help && !errorMsg && <p className="text-[10px] text-gray-500">{help}</p>}
    </div>
  );
});

// Friendly Razorpay-error mapping. The 4xx body Razorpay returns is
// developer-oriented; the modal shows a human translation + an actionable
// next step so admins know what to do without reading docs.
const RAZORPAY_FRIENDLY_ERRORS = [
  {
    match: /merchant activation form has been locked/i,
    title: 'Razorpay has locked this account for editing',
    body: (
      'This usually means the merchant activation form is under '
      + 'Razorpay\'s review or has been manually locked from the '
      + 'Razorpay Dashboard. To unlock: open the Razorpay Dashboard → '
      + 'Linked Account → click "Unlock for editing", OR contact '
      + 'Razorpay support with the account ID below. Once unlocked, '
      + 'retry the Save here.'
    ),
  },
  {
    match: /bank_account.*invalid|ifsc.*invalid/i,
    title: 'Razorpay rejected the bank details',
    body: (
      'The bank account number or IFSC code did not pass Razorpay\'s '
      + 'validation. Verify the vendor\'s bank account on the Compliance '
      + 'tab, correct it there, then return to this form.'
    ),
  },
  {
    match: /already exists|duplicate/i,
    title: 'A Linked Account already exists for this vendor',
    body: (
      'Switch to Edit mode instead of Create — the vendor already has '
      + 'a Linked Account on Razorpay.'
    ),
  },
  {
    match: /code may not be greater than 20 characters/i,
    title: 'Vendor reference ID too long',
    body: (
      'Razorpay caps the reference_id at 20 characters. This usually '
      + 'means the vendor has no `aniyor_id` set on their profile — '
      + 'the full 36-char UUID is being sent instead. Set the vendor\'s '
      + 'aniyor_id (VEN-XXXX format) on the Profile tab, then retry.'
    ),
  },
  {
    match: /subcategory.*invalid|invalid.*subcategory/i,
    title: 'Profile subcategory is not accepted by Razorpay',
    body: (
      'Razorpay only accepts specific subcategory tokens for a given '
      + 'category. Common safe values: `gifting`, `grocery`, `wholesale`, '
      + '`baby_products`, `office_supplies`. Update the Profile '
      + 'Subcategory field and retry.'
    ),
  },
  {
    // iter-233-A — Razorpay rejects 2-char state codes despite the docs
    // saying 2–32 chars. Surfaces from the LA-create payload's
    // `profile.addresses.registered.state` field.
    match: /state name.*incorrect|provide correct state name/i,
    title: 'State name was not recognised by Razorpay',
    body: (
      'Razorpay requires the FULL state name (e.g. "Maharashtra", '
      + '"Karnataka") — not a 2-char code like "MH" or "KA". Open the '
      + 'vendor\'s Profile → Business Address and replace any short '
      + 'code with the full Title-Case name, then retry. This was a '
      + 'known bug pre-iter-233-A; if you still see this after the fix '
      + 'is deployed, the vendor\'s record likely has an unusual '
      + 'spelling that needs manual correction.'
    ),
  },
];

function interpretRazorpayError(body) {
  const description = body?.body?.error?.description
    || body?.error?.description
    || body?.detail?.body?.error?.description
    || '';
  if (!description) return null;
  const friendly = RAZORPAY_FRIENDLY_ERRORS.find((f) => f.match.test(description));
  return friendly
    ? { ...friendly, raw_description: description }
    : { title: 'Razorpay rejected the request', body: description, raw_description: description };
}

const RazorpayLAFormDialog = ({
  vendorId, mode = 'create', open, onClose, onSuccess,
}) => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [form, setForm] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);

  useEffect(() => {
    if (!open || !vendorId) return;
    let active = true;
    (async () => {
      setLoading(true);
      setSubmitError(null);
      setSubmitSuccess(null);
      try {
        const res = await apiCall(`/api/admin/vendors/${vendorId}/route-account/form-prefill`);
        if (!active) return;
        setPrefill(res);
        setForm(res.editable || {});
      } catch (err) {
        if (active) {
          toast({
            title: 'Could not load form',
            description: err?.message || 'Prefill failed',
            variant: 'destructive',
          });
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [open, vendorId, apiCall, toast]);

  const dirtyFields = useMemo(() => {
    if (!prefill?.editable) return {};
    const out = {};
    Object.keys(form).forEach((k) => {
      if (form[k] !== prefill.editable[k]) out[k] = form[k];
    });
    return out;
  }, [form, prefill]);

  const requiredFields = prefill?.required_fields || [];
  const validators = prefill?.validators || {};

  const fieldError = (k) => {
    const v = form[k];
    if (requiredFields.includes(k) && (!v || String(v).trim() === '')) {
      return 'Required';
    }
    const rules = validators[k];
    if (!rules || !v) return null;
    if (rules.pattern && !new RegExp(rules.pattern).test(String(v))) {
      return 'Invalid format';
    }
    if (rules.min && String(v).length < rules.min) {
      return `Min ${rules.min} chars`;
    }
    if (rules.max && String(v).length > rules.max) {
      return `Max ${rules.max} chars`;
    }
    if (rules.enum && !rules.enum.includes(String(v))) {
      return `Must be one of: ${rules.enum.slice(0, 3).join(', ')}…`;
    }
    return null;
  };

  const hasErrors = useMemo(() => {
    return requiredFields.some((k) => fieldError(k));
  }, [form, requiredFields, fieldError]);

  // iter-231-V/W — stable onChange factory. Each field gets its own
  // memoized handler so React.memo on FieldInput actually skips
  // re-renders (no remount, no focus loss after each keystroke).
  const setField = useCallback((name) => (e) => {
    setForm((prev) => ({ ...prev, [name]: e.target.value }));
  }, []);

  // Precompute one handler per known editable key — keeps prop
  // references stable across re-renders. Recomputes only when the
  // editable shape changes (i.e. when prefill loads).
  const onChangeMap = useMemo(() => {
    const keys = Object.keys(prefill?.editable || {});
    // Always include business_type even if not in editable (it's a
    // select rendered separately from FieldInput).
    if (!keys.includes('business_type')) keys.push('business_type');
    const map = {};
    keys.forEach((k) => { map[k] = setField(k); });
    return map;
  }, [prefill, setField]);
  const getOnChange = (name) => onChangeMap[name] || setField(name);

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      let res;
      if (mode === 'edit') {
        res = await apiCall(
          `/api/admin/vendors/${vendorId}/route-account/update`,
          { method: 'POST', body: JSON.stringify(dirtyFields) },
        );
      } else {
        // Pass form corrections as overrides so the backend merges
        // them into the Razorpay payload (Bug 1 fix: previously the
        // body was empty and admin corrections were silently discarded).
        res = await apiCall(
          `/api/admin/vendors/${vendorId}/route-account/create`,
          { method: 'POST', body: JSON.stringify({ overrides: form }) },
        );
      }
      setSubmitSuccess(res);
      toast({
        title: mode === 'edit' ? 'Linked Account updated' : 'Linked Account created',
        description: res?.account_id ? `acc_id: ${res.account_id}` : 'Saved.',
      });
      if (onSuccess) onSuccess(res);
    } catch (err) {
      let body = err?.response?.detail || err?.detail || null;
      try {
        if (typeof body === 'string') body = JSON.parse(body);
      } catch { /* keep raw */ }
      setSubmitError(body || { error: err?.message || 'submit_failed' });
      toast({
        title: mode === 'edit' ? 'Update failed' : 'Create failed',
        description: body?.error || body?.message || err?.message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // FieldInput is defined at module scope (top of file) so it does not
  // re-mount on every parent render. Render helper that maps form
  // state + validators into the props the hoisted FieldInput needs.
  const renderField = (name, label, opts = {}) => (
    <FieldInput
      name={name}
      label={label}
      type={opts.type}
      help={opts.help}
      readOnly={opts.readOnly}
      required={requiredFields.includes(name)}
      value={form[name]}
      onChange={getOnChange(name)}
      errorMsg={opts.readOnly ? null : fieldError(name)}
    />
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="rzpla-form-dialog">
        <DialogHeader>
          <DialogTitle>
            {mode === 'edit'
              ? 'Edit Razorpay Linked Account'
              : 'Create Razorpay Linked Account'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'edit'
              ? 'Update fields below — only changed values will be sent to Razorpay (PATCH /v2/accounts/:id).'
              : 'Form prefilled from vendor profile. Correct any missing/invalid fields, then submit (POST /v2/accounts).'}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-8 flex items-center justify-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading vendor data…
          </div>
        )}

        {!loading && prefill && (
          <>
            {prefill.razorpay_linked_account_id && (
              <div className="flex items-center gap-2 text-xs bg-slate-50 border rounded p-2">
                <span className="text-gray-500">Razorpay LA ID:</span>
                <code className="font-mono">{prefill.razorpay_linked_account_id}</code>
                {prefill.razorpay_linked_account_status && (
                  <Badge variant="outline" className="text-[10px]">
                    {prefill.razorpay_linked_account_status}
                  </Badge>
                )}
              </div>
            )}

            {prefill.preflight_missing?.length > 0 && (
              <Alert className="bg-amber-50 border-amber-300">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertTitle>Razorpay preflight flagged missing fields</AlertTitle>
                <AlertDescription className="text-xs">
                  <ul className="list-disc list-inside">
                    {prefill.preflight_missing.map((m) => (<li key={m}>{m}</li>))}
                  </ul>
                  Fill these inputs below before submitting.
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-3">
              {renderField('email', 'Email', { type: 'email' })}
              {renderField('phone', 'Phone', { help: 'E.164 or 10-digit Indian' })}
              {renderField('legal_business_name', 'Legal Business Name')}
              {renderField('customer_facing_business_name', 'Display / Brand Name')}

              <div className="space-y-1">
                <Label htmlFor="rzpla-business_type" className="text-xs">
                  Business Type <span className="text-red-500 ml-0.5">*</span>
                </Label>
                <select
                  id="rzpla-business_type"
                  value={form.business_type ?? ''}
                  onChange={getOnChange('business_type')}
                  className="w-full h-9 px-3 rounded-md border bg-white text-sm"
                  data-testid="rzpla-field-business_type"
                >
                  <option value="">Select…</option>
                  {BUSINESS_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                {fieldError('business_type') && (
                  <p className="text-[10px] text-red-600">{fieldError('business_type')}</p>
                )}
              </div>
              {renderField('contact_name', 'Contact Name')}

              {renderField('profile_category', 'Profile Category', { help: 'e.g. ecommerce' })}
              {renderField('profile_subcategory', 'Profile Subcategory', { help: 'e.g. grocery, gifting' })}
            </div>

            <h4 className="text-xs font-semibold uppercase text-gray-500 mt-2">Registered Address</h4>
            <div className="grid grid-cols-2 gap-3">
              {renderField('street1', 'Street 1')}
              {renderField('street2', 'Street 2 (optional)')}
              {renderField('city', 'City')}
              {renderField('state', 'State')}
              {renderField('postal_code', 'Postal Code (6 digits)')}
              {renderField('country', 'Country', { help: 'IN only' })}
            </div>

            <h4 className="text-xs font-semibold uppercase text-gray-500 mt-2">
              Bank Account (read-only — attached via Stakeholder PATCH after activation)
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {renderField('bank_account_holder', 'Account Holder', { readOnly: true })}
              {renderField('bank_ifsc', 'IFSC', { readOnly: true })}
              {renderField('bank_account_number', 'Account Number', { readOnly: true })}
            </div>

            {submitError && (() => {
              const friendly = interpretRazorpayError(submitError);
              return (
                <Alert variant="destructive" data-testid="rzpla-form-error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>{friendly?.title || 'Razorpay rejected the request'}</AlertTitle>
                  <AlertDescription>
                    <p className="text-sm mb-2">{friendly?.body || friendly?.raw_description}</p>
                    {prefill?.razorpay_linked_account_id && (
                      <p className="text-xs">
                        Account ID:{' '}
                        <code className="font-mono">
                          {prefill.razorpay_linked_account_id}
                        </code>
                      </p>
                    )}
                    <details className="mt-2">
                      <summary className="text-[10px] cursor-pointer text-gray-600">
                        Show raw Razorpay response
                      </summary>
                      <pre className="text-[10px] bg-white text-gray-800 p-2 rounded overflow-auto max-h-40 mt-1">
                        {JSON.stringify(submitError, null, 2)}
                      </pre>
                    </details>
                  </AlertDescription>
                </Alert>
              );
            })()}

            {submitSuccess && (
              <Alert className="bg-emerald-50 border-emerald-200">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <AlertTitle className="text-emerald-900">Saved</AlertTitle>
                <AlertDescription>
                  <pre className="text-xs bg-white text-gray-800 p-2 rounded overflow-auto max-h-48">
                    {JSON.stringify(submitSuccess, null, 2)}
                  </pre>
                </AlertDescription>
              </Alert>
            )}

            {mode === 'edit' && Object.keys(dirtyFields).length === 0 && (
              <p className="text-xs text-gray-500 italic">
                No changes yet — edit a field to enable Save.
              </p>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || loading || hasErrors
              || (mode === 'edit' && Object.keys(dirtyFields).length === 0)}
            data-testid="rzpla-form-submit"
          >
            {submitting
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : mode === 'edit'
                ? <Save className="h-4 w-4 mr-2" />
                : <PlayCircle className="h-4 w-4 mr-2" />}
            {mode === 'edit' ? 'Save to Razorpay' : 'Create on Razorpay'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RazorpayLAFormDialog;
